import generatePicker from 'antd/es/date-picker/generatePicker';
import type { Dayjs } from 'dayjs';
import { getIn, useFormikContext } from 'formik';
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import { useRef, type FC } from 'react';
import useDatepickerHandler from '@/app/hooks/useDatepickerHandler';
import type { DatePickerProps } from './lib/types';
import { defaultFromFormikValue, defaultToFormikValue } from './lib/utils';

const AntdDatePicker = generatePicker(dayjsGenerateConfig);

const DatePicker: FC<DatePickerProps> = ({
  name,
  value,
  fromFormikValue = defaultFromFormikValue,
  toFormikValue = defaultToFormikValue,
  onChange,
  onBlur,
  onOpenChange,
  onCalendarChange,
  ...props
}) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { onBlur: handleDatepickerBlur } = useDatepickerHandler();
  const isOpenRef = useRef(false);

  const formikValue = getIn(values, name);

  return (
    <AntdDatePicker
      {...props}
      name={name}
      value={value !== undefined ? value : fromFormikValue(formikValue)}
      onChange={async (date, dateString) => {
        await setFieldValue(name, toFormikValue(dateString, date as Dayjs | null));
        await onChange?.(date, dateString);
      }}
      onCalendarChange={async (date, dateString, info) => {
        await setFieldValue(name, toFormikValue(dateString, date as Dayjs | null));
        await onCalendarChange?.(date, dateString, info);
      }}
      onOpenChange={(open) => {
        isOpenRef.current = open;
        onOpenChange?.(open);
      }}
      onBlur={async (event, info) => {
        const target = event?.target;
        const targetValue = target instanceof HTMLInputElement ? target.value : null;

        if (isOpenRef.current) {
          await onBlur?.(event, info);
          return;
        }

        await setFieldTouched(name, true);
        if (targetValue !== null) {
          await handleDatepickerBlur(name, toFormikValue(targetValue, null), setFieldValue);
        }
        await onBlur?.(event, info);
      }}
    />
  );
};

export default DatePicker;
